import { useState, useMemo } from 'react';
import { ACEITES_CATALOGO, ACEITES_GUIA_RAPIDA } from '../data/aceitesCatalogo';
import { YUASA_CATALOGO } from '../data/yuasaCatalogo';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const TAB_STYLE = (activo, color = '#4361ee') => ({
  background: 'none', border: 'none',
  borderBottom: `2px solid ${activo ? color : 'transparent'}`,
  color: activo ? color : '#555',
  fontWeight: activo ? 700 : 400,
  fontSize: 13, padding: '8px 18px', cursor: 'pointer',
});

const SEL_STYLE = (hasValue) => ({
  background: '#13131f',
  border: `1px solid ${hasValue ? '#4361ee' : '#2a2a3e'}`,
  borderRadius: 8, color: hasValue ? '#fff' : '#555',
  padding: '10px 12px', fontSize: 13, outline: 'none',
  cursor: 'pointer', width: '100%',
});

const INPUT_STYLE = (active) => ({
  background: '#13131f',
  border: `1px solid ${active ? '#4361ee' : '#2a2a3e'}`,
  borderRadius: 10, color: '#fff',
  padding: '12px 16px', fontSize: 13,
  outline: 'none', width: '100%', boxSizing: 'border-box',
});

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ label, value, color, bg }) {
  if (!value) return null;
  return (
    <div style={{ background: bg, borderRadius: 8,
      padding: '10px 14px', border: `1px solid ${color}33`,
      display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#555', fontSize: 9,
        textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ color, fontFamily: 'monospace',
        fontWeight: 700, fontSize: 14 }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB ACEITES
// ═══════════════════════════════════════════════════════════════════════════════
function TabAceites() {
  const [marca,     setMarca]     = useState('');
  const [modelo,    setModelo]    = useState('');
  const [busqLibre, setBusqLibre] = useState('');
  const [verGuia,   setVerGuia]   = useState(false);

  const marcas = useMemo(() =>
    [...new Set(ACEITES_CATALOGO.map(r => r.marca))].sort(), []);

  const modelos = useMemo(() => {
    if (!marca) return [];
    return [...new Set(
      ACEITES_CATALOGO.filter(r => r.marca === marca).map(r => r.modelo)
    )].sort();
  }, [marca]);

  const resultados = useMemo(() => {
    if (busqLibre.trim().length >= 2) {
      const q = busqLibre.toLowerCase();
      return ACEITES_CATALOGO.filter(r =>
        r.marca.toLowerCase().includes(q) ||
        r.modelo.toLowerCase().includes(q) ||
        r.motor.toLowerCase().includes(q) ||
        r.viscosidad.toLowerCase().includes(q) ||
        r.norma.toLowerCase().includes(q)
      );
    }
    if (!marca) return [];
    let res = ACEITES_CATALOGO.filter(r => r.marca === marca);
    if (modelo) res = res.filter(r => r.modelo === modelo);
    return res;
  }, [marca, modelo, busqLibre]);

  const modoLibre = busqLibre.trim().length >= 2;

  function reset() { setMarca(''); setModelo(''); setBusqLibre(''); }
  function onMarca(v) { setMarca(v); setModelo(''); }

  const COLOR_COMB = { 'Gasolina': '#4cc9f0', 'Diesel': '#ffc107', 'Híbrido': '#4caf50' };
  const BG_COMB   = { 'Gasolina': '#001a2a', 'Diesel': '#2a1e00', 'Híbrido': '#0d2218' };
  const COLOR_TIPO = { 'Sintético': '#7209b7', 'Semisintético': '#4361ee', 'Mineral/Semi': '#f72585' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Búsqueda libre */}
      <div style={{ position: 'relative' }}>
        <input
          value={busqLibre}
          onChange={e => setBusqLibre(e.target.value)}
          placeholder="Búsqueda libre: marca, modelo, motor, viscosidad, norma API..."
          style={INPUT_STYLE(modoLibre)}
        />
        {busqLibre && (
          <button onClick={() => setBusqLibre('')} style={{
            position: 'absolute', right: 12, top: '50%',
            transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: '#555',
            cursor: 'pointer', fontSize: 16,
          }}>✕</button>
        )}
      </div>

      {/* Selectores en cascada */}
      {!modoLibre && (
        <div style={{ display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ color: '#555', fontSize: 10,
              textTransform: 'uppercase', letterSpacing: 0.5,
              marginBottom: 4 }}>Marca</div>
            <select value={marca} onChange={e => onMarca(e.target.value)}
              style={SEL_STYLE(!!marca)}>
              <option value="">— Selecciona marca —</option>
              {marcas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color: '#555', fontSize: 10,
              textTransform: 'uppercase', letterSpacing: 0.5,
              marginBottom: 4 }}>Modelo</div>
            <select value={modelo} onChange={e => setModelo(e.target.value)}
              disabled={!marca} style={SEL_STYLE(!!modelo)}>
              <option value="">
                {marca ? '— Todos los modelos —' : '— Primero elige marca —'}
              </option>
              {modelos.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Contador + limpiar */}
      {(marca || modoLibre) && (
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <div style={{ color: '#555', fontSize: 12 }}>
            {resultados.length} resultado{resultados.length !== 1 ? 's' : ''}
            {modoLibre && ` para "${busqLibre}"`}
          </div>
          <button onClick={reset} style={{
            background: 'none', border: '1px solid #2a2a3e',
            borderRadius: 6, color: '#555', padding: '4px 12px',
            fontSize: 11, cursor: 'pointer',
          }}>Limpiar</button>
        </div>
      )}

      {/* Resultados */}
      {resultados.length === 0 && !marca && !modoLibre ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 48 }}>🛢️</div>
          <div style={{ color: '#555', fontSize: 13, marginTop: 12 }}>
            Selecciona una marca o escribe en la búsqueda
          </div>
          <div style={{ color: '#333', fontSize: 11, marginTop: 6 }}>
            {ACEITES_CATALOGO.length} referencias · 13 marcas
          </div>
          <button onClick={() => setVerGuia(!verGuia)} style={{
            marginTop: 16, background: '#1e1e2e',
            border: '1px solid #2a2a3e', borderRadius: 8,
            color: '#4cc9f0', padding: '8px 16px',
            fontSize: 12, cursor: 'pointer',
          }}>
            {verGuia ? '▲ Ocultar' : '📋 Ver guía rápida Ecuador'}
          </button>
        </div>
      ) : null}

      {/* Guía rápida */}
      {verGuia && !marca && !modoLibre && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14,
            marginBottom: 4 }}>📋 Guía Rápida — Ecuador</div>
          {ACEITES_GUIA_RAPIDA.map((g, i) => (
            <div key={i} style={{ background: '#1e1e2e', borderRadius: 10,
              padding: '14px 16px', border: '1px solid #2a2a3e' }}>
              <div style={{ color: '#4cc9f0', fontWeight: 700,
                fontSize: 13, marginBottom: 8 }}>{g.tipo}</div>
              <div style={{ display: 'grid',
                gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ color: '#555', fontSize: 10 }}>Aceite recomendado</div>
                  <div style={{ color: '#fff', fontFamily: 'monospace',
                    fontSize: 13, fontWeight: 700 }}>{g.aceite}</div>
                </div>
                <div>
                  <div style={{ color: '#555', fontSize: 10 }}>Intervalo Ecuador</div>
                  <div style={{ color: '#ffc107', fontSize: 12 }}>{g.intervalo}</div>
                </div>
              </div>
              <div style={{ color: '#555', fontSize: 11 }}>{g.marcas}</div>
              {g.obs && (
                <div style={{ color: '#444', fontSize: 11,
                  marginTop: 6, fontStyle: 'italic' }}>{g.obs}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cards de resultados */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {resultados.map((r, i) => {
          const colorComb = COLOR_COMB[r.combustible] || '#aaa';
          const bgComb    = BG_COMB[r.combustible]   || '#1a1a2e';
          const colorTipo = COLOR_TIPO[r.tipo]        || '#aaa';

          return (
            <div key={i} style={{ background: '#1e1e2e', borderRadius: 12,
              border: '1px solid #2a2a3e', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: '#13131f', padding: '12px 16px',
                borderBottom: '1px solid #2a2a3e',
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                    {r.marca} · {r.modelo}
                  </div>
                  {r.motor && (
                    <div style={{ color: '#aaa', fontSize: 12, marginTop: 2 }}>
                      {r.motor}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                  flexWrap: 'wrap' }}>
                  {r.anos && (
                    <span style={{ background: '#2a2a3e', color: '#4cc9f0',
                      borderRadius: 5, padding: '3px 10px', fontSize: 11,
                      fontFamily: 'monospace' }}>📅 {r.anos}</span>
                  )}
                  {r.combustible && (
                    <span style={{ background: bgComb, color: colorComb,
                      border: `1px solid ${colorComb}44`,
                      borderRadius: 5, padding: '3px 10px', fontSize: 11,
                      fontWeight: 700 }}>{r.combustible}</span>
                  )}
                  {r.tipo && (
                    <span style={{ background: '#1a1a2e', color: colorTipo,
                      border: `1px solid ${colorTipo}44`,
                      borderRadius: 5, padding: '3px 10px', fontSize: 11 }}>
                      {r.tipo}
                    </span>
                  )}
                </div>
              </div>

              {/* Datos principales */}
              <div style={{ padding: '14px 16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <Badge label="Viscosidad" value={r.viscosidad}
                  color="#4caf50" bg="#0d2218" />
                <Badge label="Norma API/ACEA" value={r.norma}
                  color="#4cc9f0" bg="#001a2a" />
                <Badge label="Capacidad motor" value={r.capacidad ? `${r.capacidad} L` : ''}
                  color="#ffc107" bg="#2a1e00" />
                <Badge label="Intervalo cambio" value={r.intervalo ? `${r.intervalo} km` : ''}
                  color="#7209b7" bg="#1a0028" />
              </div>

              {/* Notas */}
              {r.notas && (
                <div style={{ padding: '0 16px 14px' }}>
                  <div style={{ background: '#0a0a14', borderRadius: 7,
                    padding: '10px 12px',
                    borderLeft: '3px solid #2a2a3e' }}>
                    <div style={{ color: '#555', fontSize: 9,
                      textTransform: 'uppercase', letterSpacing: 1,
                      marginBottom: 4 }}>Notas</div>
                    <div style={{ color: '#888', fontSize: 12,
                      lineHeight: 1.5 }}>{r.notas}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {resultados.length > 0 && (
        <div style={{ color: '#333', fontSize: 10, textAlign: 'right',
          borderTop: '1px solid #1a1a2e', paddingTop: 8 }}>
          Referencia general · Verificar con manual del propietario ·
          {ACEITES_CATALOGO.length} registros
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB BATERIAS YUASA (mismo componente que en Garantias)
// ═══════════════════════════════════════════════════════════════════════════════
const YUASA_CATS = [
  { key: 'VEHICULOS',  label: '🚗 Vehículos',  color: '#4cc9f0' },
  { key: 'CAMIONES',   label: '🚛 Camiones',   color: '#ffc107' },
  { key: 'ELECTRICOS', label: '⚡ Eléctricos', color: '#4caf50' },
  { key: 'MAQUINARIA', label: '🏗️ Maquinaria', color: '#f72585' },
];

function BatBadge({ label, value, color, bg }) {
  if (!value) return null;
  return (
    <div style={{ background: bg, borderRadius: 8,
      padding: '10px 14px', border: `1px solid ${color}33` }}>
      <div style={{ color: '#555', fontSize: 9,
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ color, fontFamily: 'monospace',
        fontWeight: 700, fontSize: 15 }}>{value}</div>
    </div>
  );
}

function TabBaterias() {
  const [categoria, setCategoria] = useState('VEHICULOS');
  const [marca,     setMarca]     = useState('');
  const [modelo,    setModelo]    = useState('');
  const [anio,      setAnio]      = useState('');
  const [busqLibre, setBusqLibre] = useState('');

  const datosCat = useMemo(() =>
    YUASA_CATALOGO.filter(r => r.categoria === categoria && r.std), [categoria]);

  const marcas = useMemo(() =>
    [...new Set(datosCat.map(r => r.marca))].filter(Boolean).sort(), [datosCat]);

  const modelos = useMemo(() => {
    if (!marca) return [];
    return [...new Set(
      datosCat.filter(r => r.marca === marca).map(r => r.modelo)
    )].filter(Boolean).sort();
  }, [datosCat, marca]);

  const anos = useMemo(() => {
    if (!marca || !modelo) return [];
    return [...new Set(
      datosCat.filter(r => r.marca === marca && r.modelo === modelo)
        .map(r => r.ano)
    )].filter(Boolean).sort();
  }, [datosCat, marca, modelo]);

  const resultados = useMemo(() => {
    if (busqLibre.trim().length >= 2) {
      const q = busqLibre.toLowerCase();
      return datosCat.filter(r =>
        r.marca.toLowerCase().includes(q) ||
        r.modelo.toLowerCase().includes(q) ||
        r.motor?.toLowerCase().includes(q) ||
        r.std?.toLowerCase().includes(q)
      ).slice(0, 20);
    }
    if (!marca) return [];
    let res = datosCat.filter(r => r.marca === marca);
    if (modelo) res = res.filter(r => r.modelo === modelo);
    if (anio)   res = res.filter(r => r.ano === anio);
    return res;
  }, [datosCat, marca, modelo, anio, busqLibre]);

  const modoLibre = busqLibre.trim().length >= 2;

  function onCat(v)    { setCategoria(v); setMarca(''); setModelo(''); setAnio(''); }
  function onMarca(v)  { setMarca(v); setModelo(''); setAnio(''); }
  function onModelo(v) { setModelo(v); setAnio(''); }
  function reset()     { setMarca(''); setModelo(''); setAnio(''); setBusqLibre(''); }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Categoría */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {YUASA_CATS.map(c => (
          <button key={c.key} onClick={() => onCat(c.key)} style={{
            background: categoria === c.key ? `${c.color}18` : '#1e1e2e',
            border: `1px solid ${categoria === c.key ? c.color : '#2a2a3e'}`,
            borderRadius: 8, color: categoria === c.key ? c.color : '#555',
            padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>{c.label}</button>
        ))}
      </div>

      {/* Búsqueda libre */}
      <div style={{ position: 'relative' }}>
        <input value={busqLibre} onChange={e => setBusqLibre(e.target.value)}
          placeholder="Búsqueda libre: marca, modelo, motor, código batería..."
          style={INPUT_STYLE(modoLibre)} />
        {busqLibre && (
          <button onClick={() => setBusqLibre('')} style={{
            position: 'absolute', right: 12, top: '50%',
            transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: '#555',
            cursor: 'pointer', fontSize: 16,
          }}>✕</button>
        )}
      </div>

      {/* Selectores */}
      {!modoLibre && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Marca', value: marca, onChange: onMarca,
              opts: marcas, ph: '— Selecciona marca —', disabled: false },
            { label: 'Modelo', value: modelo, onChange: onModelo,
              opts: modelos,
              ph: marca ? '— Selecciona modelo —' : '— Primero elige marca —',
              disabled: !marca },
            { label: 'Año / Versión', value: anio, onChange: setAnio,
              opts: anos,
              ph: modelo ? '— Todos los años —' : '— Primero elige modelo —',
              disabled: !modelo },
          ].map(s => (
            <div key={s.label}>
              <div style={{ color: '#555', fontSize: 10,
                textTransform: 'uppercase', letterSpacing: 0.5,
                marginBottom: 4 }}>{s.label}</div>
              <select value={s.value} onChange={e => s.onChange(e.target.value)}
                disabled={s.disabled} style={{
                  ...SEL_STYLE(!!s.value),
                  opacity: s.disabled ? 0.5 : 1,
                  cursor: s.disabled ? 'not-allowed' : 'pointer',
                }}>
                <option value="">{s.ph}</option>
                {s.opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Contador */}
      {(marca || modoLibre) && (
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <div style={{ color: '#555', fontSize: 12 }}>
            {resultados.length} resultado{resultados.length !== 1 ? 's' : ''}
          </div>
          <button onClick={reset} style={{
            background: 'none', border: '1px solid #2a2a3e',
            borderRadius: 6, color: '#555', padding: '4px 12px',
            fontSize: 11, cursor: 'pointer',
          }}>Limpiar</button>
        </div>
      )}

      {/* Estado vacío */}
      {!marca && !modoLibre && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 48 }}>🔋</div>
          <div style={{ color: '#555', fontSize: 13, marginTop: 12 }}>
            Selecciona una marca o escribe en la búsqueda
          </div>
          <div style={{ color: '#333', fontSize: 11, marginTop: 6 }}>
            {datosCat.length} vehículos en {categoria}
          </div>
        </div>
      )}

      {/* Resultados */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {resultados.map((r, i) => (
          <div key={i} style={{ background: '#1e1e2e', borderRadius: 12,
            border: '1px solid #2a2a3e', overflow: 'hidden' }}>
            <div style={{ background: '#13131f', padding: '12px 16px',
              borderBottom: '1px solid #2a2a3e',
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  {r.marca} · {r.modelo}
                </div>
                {r.motor && (
                  <div style={{ color: '#aaa', fontSize: 12, marginTop: 2 }}>
                    {r.motor}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {r.ano && (
                  <span style={{ background: '#2a2a3e', color: '#4cc9f0',
                    borderRadius: 5, padding: '3px 10px',
                    fontSize: 11, fontFamily: 'monospace' }}>
                    📅 {r.ano}
                  </span>
                )}
                {r.caja && (
                  <span style={{ background: '#2a2a3e', color: '#aaa',
                    borderRadius: 5, padding: '3px 10px', fontSize: 11 }}>
                    Caja: {r.caja}
                  </span>
                )}
              </div>
            </div>
            <div style={{ padding: '14px 16px',
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <BatBadge label="STD — Estándar"
                value={r.std} color="#4cc9f0" bg="#001a2a" />
              <BatBadge label="FULL — Premium"
                value={r.full} color="#7209b7" bg="#1a0028" />
              <BatBadge label="Alternativa"
                value={r.alternativa} color="#4caf50" bg="#0d2218" />
            </div>
          </div>
        ))}
      </div>

      {resultados.length > 0 && (
        <div style={{ color: '#333', fontSize: 10, textAlign: 'right',
          borderTop: '1px solid #1a1a2e', paddingTop: 8 }}>
          Fuente: Catálogo YUASA 2026 · {YUASA_CATALOGO.length} referencias
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MÓDULO PRINCIPAL — Vehiculos
// ═══════════════════════════════════════════════════════════════════════════════
export default function Vehiculos() {
  const [tab, setTab] = useState('aceites');

  const TABS = [
    { key: 'aceites',  label: '🛢️ Guía de Aceites',   color: '#4caf50' },
    { key: 'baterias', label: '🔋 Baterías Yuasa',     color: '#4cc9f0' },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: '#e0e0e0' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: '#fff', margin: 0, fontSize: 22, fontWeight: 700 }}>
          🚗 Vehículos
        </h2>
        <div style={{ color: '#555', fontSize: 13, marginTop: 3 }}>
          Guías de referencia técnica · Ecuador / LATAM
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '1px solid #2a2a3e' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={TAB_STYLE(tab === t.key, t.color)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {tab === 'aceites'  && <TabAceites />}
      {tab === 'baterias' && <TabBaterias />}
    </div>
  );
}
